import { useMemo, useState } from 'react';
import { Button as RoccoButton } from '@rocco/components/button';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from '@rocco/components/sheet';
import { useFiltersContext } from '@rocco/ui/filter-sort/components/Filters';
import { FiltersButton } from '@rocco/ui/filter-sort/components/filters-modal/FiltersButton';
import { FiltersModalSection } from '@rocco/ui/filter-sort/components/filters-modal/FiltersModalSection';
import { formatToThousandsDelimiter } from '@rocco/utils/format-number';
import { pluralize } from '@rocco/utils/string-helpers';
import type { FilterState } from '@rocco/ui/filter-sort/view-models/filter-state/filter-state';
import { isCategoryFilterState } from '@rocco/ui/filter-sort/view-models/filter-state/filter-state';
import type {
    FilterStates,
    FilterStatesKey,
} from '@rocco/ui/filter-sort/view-models/filter-states';
import type { SortOption } from '@rocco/types/enum/sort-option';
import {
    getStagedCategoryChange,
    stageCategorySelection,
} from '@rocco/ui/filter-sort/utils/category-tree';
import { getActiveFiltersCount } from '@rocco/ui/filter-sort/utils/get-active-filters';
import { getClearedFilterStates } from '@rocco/ui/filter-sort/utils/get-cleared-filter-states';
import { FiltersModalRefine } from '@rocco/ui/filter-sort/components/filters-modal/refine/FiltersModalRefine';
import type { RefineBrowseModeConfig } from '@rocco/ui/filter-sort/components/filters-modal/refine/RefineBrowseModePills';
import { useLatest, useUpdateEffect } from 'ahooks';
import type { AggregationArgs } from '@rocco/ui/filter-sort/hooks/use-search-filters';
import { FiltersModalElement } from '@rocco/ui/filter-sort/components/filters-modal/FiltersModalElement';
import { useAtom } from 'jotai';
import { uiStateAtomFiltersModalIsOpened } from '@rocco/ui/filter-sort/states/ui-states-filters-modal';
import { DisplayMode } from '@rocco/types/enum/display-mode';
import { FiltersModalSort } from '@rocco/ui/filter-sort/components/filters-modal/FiltersModalSort';
import { getFacetFilterConfig } from '@rocco/ui/filter-sort/utils/get-facet-filter-config';

/**
 * Mobile sheet presentation. `accordion` is the classic collapsed-sections
 * sheet (legacy category pages, search). `refine` is the decision-support
 * layout (chips, pill controls, drill-in sub-views, staged category tree)
 * used by the directory browse pages. Ignored on desktop.
 */
export type FiltersModalPresentation = 'accordion' | 'refine';

export interface FiltersModalCategoryTreeConfig {
    /** Root crumb label, e.g. "All Products". */
    rootLabel: string;
    /**
     * Called as the sheet commits a staged category other than the page's
     * own, with the category it is about to navigate to.
     */
    onCommitCategory?: (category: { title: string; url: string }) => void;
}

interface FiltersModalProps {
    showSorting?: boolean;
    presentation?: FiltersModalPresentation;
    /**
     * Opt in to the inline staged category tree at the top of the refine
     * sheet. Only meaningful where the filter states carry a category tree
     * and commits land on category-page URLs (directory browse pages).
     */
    categoryTree?: FiltersModalCategoryTreeConfig;
    /**
     * Products/Brands switch, shown in the refine sheet between Sort by and
     * Filters. Omitted by entities with a single browse mode.
     */
    browseMode?: RefineBrowseModeConfig;
    /** Overrides the noun in the refine sheet's apply CTA (e.g. "Supplier"). */
    resultName?: string;
}

export const FiltersModal = (props: FiltersModalProps) => {
    const {
        showSorting = false,
        presentation = 'accordion',
        categoryTree,
        browseMode,
        resultName,
    } = props;
    const {
        searchFilters,
        displayMode,
        filterButtonVariant,
        filterButtonClassName,
        filterButtonLabel,
    } = useFiltersContext();
    const {
        config,
        filterStates,
        updateFilter,
        updateSort,
        getUpdatedFilterStates,
        handleAggregate,
        handleAggregateQueueClear,
    } = searchFilters;

    const isMobile = displayMode === DisplayMode.Mobile;
    const isRefine = isMobile && presentation === 'refine';

    // Lazy initialiser: the clone covers category trees and 100-item option
    // lists, and an inline call would re-clone on every render just to be
    // discarded.
    const [localFilterStates, setLocalFilterStates] = useState(() =>
        structuredClone(filterStates),
    );
    const facetFilterConfig = useMemo(
        () => getFacetFilterConfig(localFilterStates),
        [localFilterStates],
    );

    // Update filter states when initial states change, skipping the first render
    useUpdateEffect(() => {
        setLocalFilterStates(filterStates);
    }, [filterStates]);

    const [open, setOpen] = useAtom(uiStateAtomFiltersModalIsOpened);
    const [isLoading, setIsLoading] = useState(false);

    /**
     * Updates the local filter states only when the filter panel is open.
     * @param states - The new filter states to be applied
     */
    const updateLocalStates = (states: FilterStates) => {
        if (open) {
            setLocalFilterStates(states);
        }
    };

    /**
     * A hook that maintains a reference to the latest updateLocalStates function
     * to prevent unnecessary re-renders and ensure consistent access to the current value.
     * Used in handleUpdateStates to update filter states with aggregated data after successful API response.
     * The latest ref is needed because the callback in handleAggregate's onSuccess might close over a stale
     * updateLocalStates value from an old render if we didn't use useLatest
     */
    const latestUpdateLocalStates = useLatest(updateLocalStates);

    /**
     * Executes filter aggregation with the provided filter states
     * @param filterStates - The filter states to aggregate
     *
     * The function:
     * 1. Triggers filter aggregation with the provided states
     * 2. Shows loading indicator during aggregation
     * 3. Updates filter states with new total count on successful aggregation
     */
    const executeAggregate = (
        filterStates: FilterStates,
        opts?: { syncFullResponse?: boolean },
    ) => {
        handleAggregate({
            aggregateType: 'filterAggregation',
            filterStates: filterStates,
            // Plain toggles only read totalCount from the response, so they
            // skip the facet/range aggregation suite on the search service.
            // Category staging adopts the full rebuilt response and needs
            // the whole suite.
            scope: opts?.syncFullResponse ? 'full' : 'count',
            onStart: () => setIsLoading(true),
            onFinish: () => setIsLoading(false),
            onSuccess: (data) => {
                const sync = latestUpdateLocalStates.current;
                if (!sync) return;

                if (opts?.syncFullResponse) {
                    // Category staging: adopt the rebuilt, category-scoped
                    // response (fresh facet option lists, ranges, counts).
                    // Standard filter selections survive because they
                    // round-trip through the URL the aggregate request is
                    // built from. Facet selections only survive while the
                    // pending category satisfies the facet depth rule —
                    // when staging up past it the response omits the facet
                    // group and its chips drop with it, which is intended:
                    // those filters no longer exist at that category scope.
                    // The staged category object (with its companion
                    // categoryLink) and staged sort are kept — the aggregate
                    // endpoint echoes a default sort, omits categoryLink,
                    // and its tree may be pruned to the pending scope.
                    sync({
                        ...data,
                        sortBy: filterStates.sortBy ?? data.sortBy,
                        categoryLink: filterStates.categoryLink,
                        states: {
                            ...data.states,
                            category: filterStates.states.category,
                        },
                    });
                } else {
                    sync({
                        ...filterStates,
                        totalCount: data.totalCount,
                    });
                }
            },
        });
    };

    /**
     * Updates filter states and optionally triggers data aggregation
     * @param filterKey - Key identifying the filter to update in the filter states object
     * @param newState - New state object containing updated values for the specified filter
     * @param shouldAggregate - Flag to control whether data aggregation should be triggered after state update (defaults to true)
     *
     * The function:
     * 1. Updates the local filter states with the new state for the specified filter key
     * 2. If shouldAggregate is true:
     *    - Triggers filter aggregation with updated states
     *    - Shows loading indicator during aggregation
     *    - Updates filter states with new total count on successful aggregation
     */
    const handleUpdateStates = (
        filterKey: FilterStatesKey,
        newState: FilterState,
        shouldAggregate: boolean = isMobile,
        isFacetFilter: boolean = false,
    ) => {
        const updatedFilterStates = getUpdatedFilterStates(
            localFilterStates,
            filterKey,
            newState,
            isFacetFilter,
        );

        setLocalFilterStates(updatedFilterStates);

        if (shouldAggregate) {
            executeAggregate(updatedFilterStates);
        }

        if (!isMobile) {
            updateFilter(updatedFilterStates);
        }
    };

    const handleUpdateFacetStates = (
        filterKey: FilterStatesKey,
        newState: FilterState,
        shouldAggregate: boolean = isMobile,
    ) => handleUpdateStates(filterKey, newState, shouldAggregate, true);

    const handleAlphabeticalGrouping = (args: AggregationArgs) => {
        handleAggregate({
            ...args,
            filterStates: localFilterStates,
        });
    };

    const reportCategoryCommit = () => {
        const committed = filterStates.states.category;
        const staged = localFilterStates.states.category;
        if (!categoryTree?.onCommitCategory) return;
        if (!committed || !isCategoryFilterState(committed)) return;
        if (!staged || !isCategoryFilterState(staged)) return;

        const next = getStagedCategoryChange(committed, staged);
        if (next) categoryTree.onCommitCategory(next);
    };

    const handleUpdateFilter = () => {
        reportCategoryCommit();
        updateFilter(localFilterStates);
        setOpen(false);
    };

    /**
     * Stage a pending category selection and refresh the sheet against it.
     * Nothing navigates until the commit CTA; the aggregate request is
     * scoped by the staged category via the URL it is built from.
     */
    const handleStageCategory = (url: string) => {
        const stagedCategory = localFilterStates.states.category;
        if (!stagedCategory || !isCategoryFilterState(stagedCategory)) return;

        const updatedCategory = stageCategorySelection(stagedCategory, url);
        if (!updatedCategory) return;

        const updated: FilterStates = {
            ...localFilterStates,
            categoryLink: updatedCategory.selectedCategory?.url,
            states: {
                ...localFilterStates.states,
                category: updatedCategory,
            },
        };

        setLocalFilterStates(updated);
        executeAggregate(updated, { syncFullResponse: true });
    };

    /**
     * Stage a sort choice locally so it commits with the CTA. Deliberately
     * not `updateSort`: writing to the hook's committed states re-syncs the
     * sheet's local copy and would wipe staged-but-uncommitted selections.
     */
    const handleStageSort = (sort: SortOption) => {
        setLocalFilterStates((prev) => ({ ...prev, sortBy: sort }));
    };

    /**
     * Refine-sheet clear: resets filters from the committed states (so a
     * staged category reverts to the page's actual category) while keeping
     * the staged sort — sort is not a refinement.
     */
    const handleClearFiltersRefine = () => {
        const cleared = getClearedFilterStates(filterStates);
        const clearedKeepingStagedSort: FilterStates = {
            ...cleared,
            sortBy: localFilterStates.sortBy ?? cleared.sortBy,
        };

        handleAggregateQueueClear();
        setLocalFilterStates(clearedKeepingStagedSort);
        executeAggregate(clearedKeepingStagedSort);
    };

    const handleClearFilters = () => {
        const emptyFilterStates = getClearedFilterStates(filterStates);

        handleAggregateQueueClear();
        setLocalFilterStates(emptyFilterStates);

        // Apply cleared filters immediately on desktop, or aggregate on mobile
        if (!isMobile) {
            updateFilter(emptyFilterStates);
        } else {
            executeAggregate(emptyFilterStates);
        }
    };

    const totalActiveFilters = getActiveFiltersCount(localFilterStates);
    const totalCount = localFilterStates.totalCount ?? 0;

    return (
        <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
                <FiltersButton
                    activeFiltersCount={totalActiveFilters}
                    displayMode={displayMode}
                    variant={filterButtonVariant}
                    buttonClassName={filterButtonClassName}
                    label={filterButtonLabel}
                />
            </SheetTrigger>
            {/* Will close the modal when clicking outside the modal content */}
            <SheetContent
                // Refine (browse revamp) uses the white theme; the classic
                // accordion sheet keeps the legacy frosted-grey mobile skin.
                color={isRefine || !isMobile ? 'opaque' : 'default'}
                side={isMobile ? 'bottom' : 'left'}
                onPointerDownOutside={() => setOpen(false)}
                closeContent={() => setOpen(false)}
            >
                {isRefine ? (
                    <FiltersModalRefine
                        config={config}
                        facetSections={facetFilterConfig}
                        localFilterStates={localFilterStates}
                        isLoading={isLoading}
                        showSorting={showSorting}
                        categoryTree={categoryTree}
                        browseMode={browseMode}
                        resultName={resultName}
                        onUpdateFilterState={handleUpdateStates}
                        onUpdateFacetFilterState={handleUpdateFacetStates}
                        onAlphabeticalGrouping={handleAlphabeticalGrouping}
                        onStageSort={handleStageSort}
                        onStageCategory={handleStageCategory}
                        onClearFilters={handleClearFiltersRefine}
                        onApply={handleUpdateFilter}
                    />
                ) : (
                    <>
                        {/* Desktop: outer flex column fills modal height; header stays at top for close button alignment */}
                        <div className="md:flex md:h-full md:w-[370px] md:flex-col qhd:w-[440px]">
                            <SheetHeader>
                                <SheetTitle>All Filters</SheetTitle>
                                <SheetDescription className="hidden" />
                            </SheetHeader>
                            {/* Body+footer container: flex-1 fills remaining height, inner flex-col for scroll+footer */}
                            <div className="md:flex md:min-h-0 md:flex-1 md:flex-col">
                                <div className="h-[70svh] max-h-[70svh] scrollbar-hidden w-dvw overflow-y-scroll [overflow-anchor:none] md:h-auto md:max-h-none md:min-h-0 md:w-auto md:flex-1 md:overflow-y-auto md:px-6">
                                    {showSorting && isMobile && (
                                        <FiltersModalSort
                                            sort={filterStates.sortBy}
                                            onSortChange={(sort) =>
                                                updateSort(sort, !isMobile)
                                            }
                                        />
                                    )}

                                    {config.modalFilters.map(
                                        (filterSection) => (
                                            <FiltersModalSection
                                                key={`filter-${filterSection.title}`}
                                                sectionConfig={filterSection}
                                                filterStates={localFilterStates}
                                                displayMode={displayMode}
                                            >
                                                {filterSection.filters.map(
                                                    (filterConfigItem) => (
                                                        <FiltersModalElement
                                                            key={
                                                                filterConfigItem.filterKey
                                                            }
                                                            displayMode={
                                                                displayMode
                                                            }
                                                            filterConfigItem={
                                                                filterConfigItem
                                                            }
                                                            filterState={
                                                                localFilterStates
                                                                    .states[
                                                                    filterConfigItem
                                                                        .filterKey
                                                                ] ?? null
                                                            }
                                                            onUpdateFilterState={
                                                                handleUpdateStates
                                                            }
                                                            onAlphabeticalGrouping={
                                                                handleAlphabeticalGrouping
                                                            }
                                                        />
                                                    ),
                                                )}
                                            </FiltersModalSection>
                                        ),
                                    )}
                                    {facetFilterConfig.map((filterSection) => (
                                        <FiltersModalSection
                                            key={`filter-${filterSection.title}`}
                                            sectionConfig={filterSection}
                                            filterStates={localFilterStates}
                                            displayMode={displayMode}
                                        >
                                            {filterSection.filters.map(
                                                (filterConfigItem) => (
                                                    <FiltersModalElement
                                                        key={
                                                            filterConfigItem.filterKey
                                                        }
                                                        displayMode={
                                                            displayMode
                                                        }
                                                        filterConfigItem={
                                                            filterConfigItem
                                                        }
                                                        filterState={
                                                            localFilterStates.facet?.facetOptions?.find(
                                                                (facet) =>
                                                                    facet.id ===
                                                                    filterConfigItem.filterKey,
                                                            )?.state ?? null
                                                        }
                                                        onUpdateFilterState={
                                                            handleUpdateFacetStates
                                                        }
                                                        onAlphabeticalGrouping={
                                                            handleAlphabeticalGrouping
                                                        }
                                                    />
                                                ),
                                            )}
                                        </FiltersModalSection>
                                    ))}
                                </div>

                                {!isMobile && (
                                    <SheetFooter className="shrink-0 rounded-b-2xl border-t border-gray-900/30 bg-white/95 shadow-lg backdrop-blur">
                                        <div className="flex w-full items-center justify-end px-6 py-4">
                                            <RoccoButton
                                                type="button"
                                                color="dark"
                                                onClick={handleClearFilters}
                                                className="cursor-pointer text-sm"
                                            >
                                                Clear All
                                            </RoccoButton>
                                        </div>
                                    </SheetFooter>
                                )}
                            </div>
                        </div>

                        {isMobile && (
                            <SheetFooter>
                                <div className="flex w-full items-center justify-between">
                                    <RoccoButton
                                        color="transparent"
                                        size="compact"
                                        onClick={handleClearFilters}
                                    >
                                        Clear All
                                    </RoccoButton>
                                    <RoccoButton
                                        color="secondary"
                                        inverted
                                        onClick={handleUpdateFilter}
                                        isLoading={isLoading}
                                        disabled={totalCount === 0}
                                    >
                                        {totalCount > 0
                                            ? `Show ${formatToThousandsDelimiter(totalCount)} ${pluralize(totalCount, config.resultName, true)}`
                                            : 'No exact matches'}
                                    </RoccoButton>
                                </div>
                            </SheetFooter>
                        )}
                    </>
                )}
            </SheetContent>
        </Sheet>
    );
};

// Re-exported from their new home so existing import sites keep working.
export {
    getActiveFiltersCount,
    isFilterStateActive,
} from '@rocco/ui/filter-sort/utils/get-active-filters';
